import { Head, Link, router } from "@inertiajs/react";
import type { FormDataConvertible } from "@inertiajs/core";
import {
  Badge,
  Box,
  Button,
  Callout,
  DropdownMenu,
  Flex,
  IconButton,
  Popover,
  Select,
  Spinner,
  Table,
  Text,
  TextField,
} from "@radix-ui/themes";
import { Fragment, useEffect, useMemo, useState } from "react";

import { AppLayout } from "@/components/app-layout";
import { searchIntegrationOptions, type IntegrationOption } from "@/lib/api";
import type { SharedProps } from "@/lib/types";

type ProjectRow = {
  id: number;
  name: string;
  status: string;
  project_group_id: number | null;
  project_group_name: string | null;
  project_owner_id: number | null;
  project_manager_id: number | null;
  frontend_lead_id: number | null;
  backend_lead_id: number | null;
  project_owner_email: string | null;
  project_manager_email: string | null;
  frontend_lead_email: string | null;
  backend_lead_email: string | null;
  harvest_project_id: string | null;
  harvest_url: string | null;
  asana_project_id: string | null;
  quickbooks_customer_id: string | null;
  google_drive_folder_id: string | null;
  vcs_repositories: Array<{ provider: string; external_id: string; name: string; url: string }>;
  repository_provider: string;
  mapping_completeness: "complete" | "incomplete";
  recommended_asana: { id?: string; name?: string; confidence?: number } | Record<string, never>;
  recommended_quickbooks:
    | { id?: string; name?: string; confidence?: number }
    | Record<string, never>;
  newly_synced: boolean;
};

type ProjectsIndexProps = SharedProps & {
  projects: ProjectRow[];
  users: Array<{ id: number; email: string }>;
  projectGroups: Array<{ id: number; name: string; group_type: "regular" | "roll_up" }>;
  filters: {
    search: string;
    mapping_completeness: string;
    repository_provider: string;
  };
  syncSummary?: {
    processed_count: number;
    new_count: number;
    archived_count: number;
    archived_projects?: string[];
    skipped_count?: number;
  };
  bulkGroupSummary?: {
    moved_count: number;
    failed_count: number;
    failures: Array<{ id: number; reason: string }>;
  };
  newlyCreatedProjectIds: number[];
  harvestSyncReady: boolean;
  harvestSyncReason: string | null;
  quickbooksOptions?: IntegrationOption[];
  asanaOptions?: IntegrationOption[];
};

const SUGGESTIONS_LIMIT = 50;
type ProjectUpdatePayload = Record<string, FormDataConvertible>;

function OptionsList({
  options,
  inputValue,
  onSelect,
  labelFn,
}: {
  options: IntegrationOption[];
  inputValue: string;
  onSelect: (option: IntegrationOption) => void;
  labelFn: (option: IntegrationOption) => string;
}) {
  const term = inputValue.trim().toLowerCase();
  const filtered = term
    ? options.filter((o) => labelFn(o).toLowerCase().includes(term)).slice(0, SUGGESTIONS_LIMIT)
    : options.slice(0, SUGGESTIONS_LIMIT);

  if (filtered.length === 0 && term) {
    return (
      <Text size="1" color="gray">
        No matches
      </Text>
    );
  }
  if (filtered.length === 0) return null;

  return (
    <div style={{ maxHeight: 200, overflowY: "auto" }} data-testid="options-list">
      <Flex direction="column" gap="0">
        {filtered.map((option) => (
          <button
            key={option.id}
            type="button"
            onClick={() => onSelect(option)}
            style={{
              all: "unset",
              cursor: "pointer",
              padding: "4px 8px",
              borderRadius: 4,
              fontSize: 13,
            }}
            className="hover:bg-[var(--accent-a3)]"
          >
            {labelFn(option)}
          </button>
        ))}
      </Flex>
    </div>
  );
}

function queryString(filters: ProjectsIndexProps["filters"]) {
  const params = new URLSearchParams();
  if (filters.search) params.set("search", filters.search);
  if (filters.mapping_completeness)
    params.set("mapping_completeness", filters.mapping_completeness);
  if (filters.repository_provider) params.set("repository_provider", filters.repository_provider);
  return params.toString();
}

export default function ProjectsIndex({
  auth,
  projects,
  users,
  projectGroups,
  filters: initialFilters,
  syncSummary,
  bulkGroupSummary,
  newlyCreatedProjectIds,
  harvestSyncReady,
  harvestSyncReason,
  flash,
  quickbooksOptions: serverQbOptions,
  asanaOptions: serverAsanaOptions,
}: ProjectsIndexProps) {
  const [filters, setFilters] = useState(initialFilters);
  const [syncing, setSyncing] = useState(false);
  const [activeProjectAction, setActiveProjectAction] = useState<number | null>(null);
  const [integrationOptions, setIntegrationOptions] = useState<{
    asana: IntegrationOption[];
    quickbooks: IntegrationOption[];
    vcs: IntegrationOption[];
  }>({
    asana: serverAsanaOptions || [],
    quickbooks: serverQbOptions || [],
    vcs: [],
  });
  const [vcsLoading, setVcsLoading] = useState(false);
  const [optionsError, setOptionsError] = useState<string | null>(null);
  const [fieldInput, setFieldInput] = useState<Record<string, string>>({});
  const [repoProviderByProject, setRepoProviderByProject] = useState<
    Record<number, "github" | "bitbucket">
  >({});
  const [selectedProjectIds, setSelectedProjectIds] = useState<number[]>([]);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [focusedProjectId, setFocusedProjectId] = useState<number | null>(null);

  const isAdmin = auth.user?.roles.includes("administrator");

  function toggleGroupExpansion(groupName: string) {
    setExpandedGroups((current) => {
      const next = new Set(current);
      if (next.has(groupName)) {
        next.delete(groupName);
      } else {
        next.add(groupName);
      }
      return next;
    });
  }

  useEffect(() => {
    let cancelled = false;

    setVcsLoading(true);
    searchIntegrationOptions("vcs", "")
      .then((vcs) => {
        if (!cancelled) setIntegrationOptions((current) => ({ ...current, vcs }));
      })
      .catch(() => {
        if (!cancelled) setOptionsError("Unable to load VCS options. Refresh to retry.");
      })
      .finally(() => {
        if (!cancelled) setVcsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const rawFocusProjectId = params.get("focus_project_id");
    if (!rawFocusProjectId) return;

    const projectId = Number(rawFocusProjectId);
    if (!Number.isFinite(projectId)) return;

    const target = document.getElementById(`project-row-${projectId}`);
    if (!target) return;

    target.scrollIntoView({ behavior: "smooth", block: "center" });
    setFocusedProjectId(projectId);
    window.setTimeout(
      () => setFocusedProjectId((current) => (current === projectId ? null : current)),
      2400,
    );
  }, [projects]);

  const optionsByProvider = useMemo(
    () => ({
      github: integrationOptions.vcs.filter(
        (option) => (option.provider || "").toLowerCase() === "github",
      ),
      bitbucket: integrationOptions.vcs.filter(
        (option) => (option.provider || "").toLowerCase() === "bitbucket",
      ),
    }),
    [integrationOptions.vcs],
  );

  const qbOptionsById = useMemo(() => {
    const map: Record<string, IntegrationOption> = {};
    for (const option of integrationOptions.quickbooks) {
      map[option.id] = option;
    }
    return map;
  }, [integrationOptions.quickbooks]);

  const groupedProjects = useMemo(() => {
    const sorted = [...projects].sort((a, b) => {
      const groupCompare = (a.project_group_name || "Unassigned").localeCompare(
        b.project_group_name || "Unassigned",
        undefined,
        { sensitivity: "base" },
      );
      if (groupCompare !== 0) return groupCompare;
      return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
    });

    const groups = new Map<string, ProjectRow[]>();
    sorted.forEach((project) => {
      const key = project.project_group_name || "Unassigned";
      groups.set(key, [...(groups.get(key) || []), project]);
    });

    return Array.from(groups.entries());
  }, [projects]);

  function applyFilters() {
    router.get("/projects", filters, { preserveState: true });
  }

  function syncProjects() {
    if (!harvestSyncReady) return;

    const startedAt = Date.now();
    setSyncing(true);
    router.post(
      `/projects/sync${queryString(filters) ? `?${queryString(filters)}` : ""}`,
      {},
      {
        preserveScroll: true,
        onFinish: () => {
          const elapsed = Date.now() - startedAt;
          const remaining = Math.max(0, 500 - elapsed);
          window.setTimeout(() => setSyncing(false), remaining);
        },
      },
    );
  }

  function toggleProjectSelection(projectId: number, checked: boolean) {
    setSelectedProjectIds((current) => {
      if (checked) return Array.from(new Set([...current, projectId]));
      return current.filter((id) => id !== projectId);
    });
  }

  function toggleSelectAll(checked: boolean) {
    if (!checked) {
      setSelectedProjectIds([]);
      return;
    }
    setSelectedProjectIds(projects.map((project) => project.id));
  }

  function bulkAssignGroup(targetGroupId: number) {
    if (!selectedProjectIds.length) return;
    router.post(
      `/projects/bulk_assign_group${queryString(filters) ? `?${queryString(filters)}` : ""}`,
      {
        project_group_id: targetGroupId,
        project_ids: selectedProjectIds,
      },
      {
        preserveScroll: true,
        onFinish: () => setSelectedProjectIds([]),
      },
    );
  }

  function updateProject(projectId: number, payload: ProjectUpdatePayload) {
    setActiveProjectAction(projectId);
    router.patch(
      `/projects/${projectId}${queryString(filters) ? `?${queryString(filters)}` : ""}`,
      {
        project: payload,
        from: "settings",
      },
      {
        preserveState: true,
        preserveScroll: true,
        replace: true,
        onFinish: () => setActiveProjectAction(null),
      },
    );
  }

  function projectRepositories(project: ProjectRow) {
    return Array.isArray(project.vcs_repositories) ? project.vcs_repositories : [];
  }

  function optionLabel(option: IntegrationOption) {
    return `${option.name} (${option.id})`;
  }

  function resolveOption(options: IntegrationOption[], value: string) {
    const normalized = value.trim().toLowerCase();
    if (!normalized) return null;

    const exact = options.find((option) => {
      const label = optionLabel(option).toLowerCase();
      return (
        label === normalized ||
        option.id.toLowerCase() === normalized ||
        option.name.toLowerCase() === normalized
      );
    });
    if (exact) return exact;

    const partialMatches = options.filter((option) => {
      const optionName = option.name.toLowerCase();
      return (
        optionName.includes(normalized) ||
        optionName.startsWith(normalized) ||
        option.id.toLowerCase() === normalized
      );
    });

    return partialMatches.length === 1 ? partialMatches[0] : null;
  }

  function resolveManualRepositoryOption(
    provider: "github" | "bitbucket",
    value: string,
  ): IntegrationOption | null {
    const input = value.trim();
    if (!input) return null;

    if (provider === "github") {
      const githubUrlMatch = input.match(
        /^https?:\/\/github\.com\/([^/\s]+)\/([^/\s?#]+?)(?:\.git)?(?:[/?#].*)?$/i,
      );
      const slugMatch = input.match(/^([^/\s]+)\/([^/\s]+)$/);
      const owner = githubUrlMatch?.[1] || slugMatch?.[1];
      const repoRaw = githubUrlMatch?.[2] || slugMatch?.[2];
      if (!owner || !repoRaw) return null;
      const repo = repoRaw.replace(/\.git$/i, "");
      const fullName = `${owner}/${repo}`;
      return {
        id: fullName,
        provider: "github",
        name: fullName,
        url: `https://github.com/${fullName}`,
      };
    }

    const bitbucketUrlMatch = input.match(
      /^https?:\/\/bitbucket\.org\/([^/\s]+)\/([^/\s?#]+?)(?:\.git)?(?:[/?#].*)?$/i,
    );
    const slugMatch = input.match(/^([^/\s]+)\/([^/\s]+)$/);
    const workspace = bitbucketUrlMatch?.[1] || slugMatch?.[1];
    const repoRaw = bitbucketUrlMatch?.[2] || slugMatch?.[2];
    if (!workspace || !repoRaw) return null;
    const repo = repoRaw.replace(/\.git$/i, "");
    const fullName = `${workspace}/${repo}`;
    return {
      id: fullName,
      provider: "bitbucket",
      name: fullName,
      url: `https://bitbucket.org/${fullName}`,
    };
  }

  function formatLeadName(email: string | null | undefined) {
    if (!email) return "Unassigned";
    const local = email.split("@")[0];
    if (!local) return email;

    // Handle names like "john.doe" or "jane_smith" or "first-last"
    const separators = /[._-]/;
    const parts = local.split(separators).filter(Boolean);

    if (parts.length === 0) return email;

    // Capitalize first name
    const firstName = parts[0].charAt(0).toUpperCase() + parts[0].slice(1).toLowerCase();

    // If there's a second part, use it as last initial
    if (parts.length > 1) {
      const lastInitial = parts[1].charAt(0).toUpperCase();
      return `${firstName} ${lastInitial}.`;
    }

    return firstName;
  }

  function leadValue(project: ProjectRow, role: "po" | "pm" | "fe" | "be") {
    switch (role) {
      case "po":
        return project.project_owner_id?.toString() || "";
      case "pm":
        return project.project_manager_id?.toString() || "";
      case "fe":
        return project.frontend_lead_id?.toString() || "";
      case "be":
        return project.backend_lead_id?.toString() || "";
      default:
        return "";
    }
  }

  function leadEmail(project: ProjectRow, role: "po" | "pm" | "fe" | "be") {
    switch (role) {
      case "po":
        return project.project_owner_email;
      case "pm":
        return project.project_manager_email;
      case "fe":
        return project.frontend_lead_email;
      case "be":
        return project.backend_lead_email;
      default:
        return null;
    }
  }

  return (
    <>
      <Head title="Projects" />
      <AppLayout
        user={auth.user}
        activeModule="weekly_planner"
        onModuleChange={() => {}}
        pageTitle="Projects"
        contextControls={
          <Flex gap="3" wrap="wrap" align="end">
            <div>
              <Text as="label" size="2" weight="medium">
                Search name
              </Text>
              <TextField.Root
                mt="1"
                value={filters.search}
                onChange={(event) =>
                  setFilters((current) => ({ ...current, search: event.target.value }))
                }
                placeholder="Search project"
              />
            </div>
            <div>
              <Text as="label" size="2" weight="medium">
                Mapping Completeness
              </Text>
              <Select.Root
                value={filters.mapping_completeness || "__all__"}
                onValueChange={(value) =>
                  setFilters((current) => ({
                    ...current,
                    mapping_completeness: value === "__all__" ? "" : value,
                  }))
                }
              >
                <Select.Trigger mt="1" />
                <Select.Content>
                  <Select.Item value="__all__">All</Select.Item>
                  <Select.Item value="complete">Complete</Select.Item>
                  <Select.Item value="incomplete">Incomplete</Select.Item>
                </Select.Content>
              </Select.Root>
            </div>
            <div>
              <Text as="label" size="2" weight="medium">
                Repository Provider
              </Text>
              <Select.Root
                value={filters.repository_provider || "__all__"}
                onValueChange={(value) =>
                  setFilters((current) => ({
                    ...current,
                    repository_provider: value === "__all__" ? "" : value,
                  }))
                }
              >
                <Select.Trigger mt="1" />
                <Select.Content>
                  <Select.Item value="__all__">All</Select.Item>
                  <Select.Item value="github">GitHub</Select.Item>
                  <Select.Item value="bitbucket">Bitbucket</Select.Item>
                  <Select.Item value="mixed">Mixed</Select.Item>
                  <Select.Item value="none">None</Select.Item>
                </Select.Content>
              </Select.Root>
            </div>
            <Button variant="soft" onClick={applyFilters}>
              Apply filters
            </Button>
          </Flex>
        }
        pageActions={
          <Flex gap="2">
            <Link href="/">
              <Button variant="outline">Back to Dashboard</Button>
            </Link>
            {isAdmin && (
              <Button onClick={syncProjects} disabled={syncing || !harvestSyncReady}>
                {syncing ? (
                  <>
                    <Spinner size="1" className="mr-2" />
                    Syncing...
                  </>
                ) : (
                  "Sync Projects"
                )}
              </Button>
            )}
            {isAdmin && (
              <DropdownMenu.Root>
                <DropdownMenu.Trigger>
                  <Button variant="soft" disabled={selectedProjectIds.length === 0}>
                    Project Group
                  </Button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Content>
                  <DropdownMenu.Label>Add to Project Group</DropdownMenu.Label>
                  <DropdownMenu.Separator />
                  {projectGroups.map((group) => (
                    <DropdownMenu.Item key={group.id} onSelect={() => bulkAssignGroup(group.id)}>
                      {group.name}
                    </DropdownMenu.Item>
                  ))}
                </DropdownMenu.Content>
              </DropdownMenu.Root>
            )}
          </Flex>
        }
      >
        <div className="space-y-4">
          {flash.notice && (
            <Callout.Root color="green" variant="soft">
              <Callout.Text>{flash.notice}</Callout.Text>
            </Callout.Root>
          )}
          {flash.alert && (
            <Callout.Root color="red" variant="soft">
              <Callout.Text>{flash.alert}</Callout.Text>
            </Callout.Root>
          )}
          {optionsError && (
            <Callout.Root color="amber" variant="soft">
              <Callout.Text>{optionsError}</Callout.Text>
            </Callout.Root>
          )}
          {!harvestSyncReady && harvestSyncReason && (
            <Callout.Root color="amber" variant="soft">
              <Callout.Text>{harvestSyncReason}</Callout.Text>
            </Callout.Root>
          )}
          {syncSummary && (
            <div className="space-y-2">
              <Flex gap="4" wrap="wrap">
                <Badge color="blue">Processed: {syncSummary.processed_count}</Badge>
                <Badge color="green">New: {syncSummary.new_count}</Badge>
                <Badge color="orange">Archived: {syncSummary.archived_count}</Badge>
                {!!syncSummary.skipped_count && (
                  <Badge color="amber">Skipped: {syncSummary.skipped_count}</Badge>
                )}
              </Flex>
              {!!syncSummary.archived_projects?.length && (
                <Text size="1" color="gray">
                  Archived projects: {syncSummary.archived_projects.join(", ")}
                </Text>
              )}
            </div>
          )}
          {bulkGroupSummary && (
            <Callout.Root
              color={bulkGroupSummary.failed_count > 0 ? "amber" : "green"}
              variant="soft"
            >
              <Callout.Text>
                Moved {bulkGroupSummary.moved_count} project(s)
                {bulkGroupSummary.failed_count > 0
                  ? ` | Failed: ${bulkGroupSummary.failed_count}`
                  : ""}
              </Callout.Text>
            </Callout.Root>
          )}
          <Table.Root variant="surface">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>
                  <input
                    aria-label="Select all projects"
                    type="checkbox"
                    checked={projects.length > 0 && selectedProjectIds.length === projects.length}
                    onChange={(event) => toggleSelectAll(event.target.checked)}
                  />
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell style={{ width: "20%" }}>Name</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell style={{ width: "15%" }}>Asana</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell style={{ width: "12%" }}>Repos</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell style={{ width: "18%" }}>Leads</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell style={{ width: "15%" }}>
                  Project Group
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell style={{ width: "15%" }}>QB Client</Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {groupedProjects.map(([groupName, groupProjects]) => (
                <Fragment key={`group-${groupName}`}>
                  <Table.Row key={`group-${groupName}`}>
                    <Table.Cell colSpan={7}>
                      <Flex align="center" gap="2">
                        <IconButton
                          size="1"
                          variant="ghost"
                          onClick={() => toggleGroupExpansion(groupName)}
                          aria-label={
                            expandedGroups.has(groupName)
                              ? `Collapse ${groupName}`
                              : `Expand ${groupName}`
                          }
                        >
                          {expandedGroups.has(groupName) ? (
                            <svg
                              width="15"
                              height="15"
                              viewBox="0 0 15 15"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M3.13523 6.15803C3.3241 5.95657 3.64052 5.94637 3.84197 6.13523L7.5 9.56464L11.158 6.13523C11.3595 5.94637 11.6759 5.95657 11.8648 6.15803C12.0536 6.35949 12.0434 6.67591 11.842 6.86477L7.84197 10.6148C7.64964 10.7951 7.35036 10.7951 7.15803 10.6148L3.15803 6.86477C2.95657 6.67591 2.94637 6.35949 3.13523 6.15803Z"
                                fill="currentColor"
                                fillRule="evenodd"
                                clipRule="evenodd"
                              />
                            </svg>
                          ) : (
                            <svg
                              width="15"
                              height="15"
                              viewBox="0 0 15 15"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M6.1584 3.13508C6.35985 2.94621 6.67627 2.95631 6.86514 3.15776L10.6151 7.15776C10.7954 7.35009 10.7954 7.64936 10.6151 7.84169L6.86514 11.8417C6.67627 12.0431 6.35985 12.0532 6.1584 11.8644C5.95694 11.6755 5.94684 11.3591 6.13571 11.1576L9.565 7.49973L6.13571 3.84183C5.94684 3.64038 5.95694 3.32395 6.1584 3.13508Z"
                                fill="currentColor"
                                fillRule="evenodd"
                                clipRule="evenodd"
                              />
                            </svg>
                          )}
                        </IconButton>
                        <Text weight="bold" color="gray">
                          {groupName}
                        </Text>
                        <Text size="1" color="gray">
                          ({groupProjects.length})
                        </Text>
                      </Flex>
                    </Table.Cell>
                  </Table.Row>
                  {!expandedGroups.has(groupName) &&
                    groupProjects.map((project) => {
                      const asanaKey = `asana-${project.id}`;
                      const qbKey = `qb-${project.id}`;
                      const vcsKey = `vcs-${project.id}`;
                      const repos = projectRepositories(project);
                      const recommendedAsana = project.recommended_asana as {
                        id?: string;
                        name?: string;
                        confidence?: number;
                      };
                      const recommendedQb = project.recommended_quickbooks as {
                        id?: string;
                        name?: string;
                        confidence?: number;
                      };
                      const selectedProvider = repoProviderByProject[project.id] || "github";
                      const currentAsanaOption = integrationOptions.asana.find(
                        (option) => option.id === project.asana_project_id,
                      );
                      const currentQbOption = project.quickbooks_customer_id
                        ? qbOptionsById[project.quickbooks_customer_id] || null
                        : null;
                      const asanaInput =
                        fieldInput[asanaKey] ??
                        (currentAsanaOption
                          ? optionLabel(currentAsanaOption)
                          : project.asana_project_id || "");
                      const qbInput =
                        fieldInput[qbKey] ??
                        (currentQbOption
                          ? optionLabel(currentQbOption)
                          : project.quickbooks_customer_id || "");
                      const repoInput = fieldInput[vcsKey] || "";
                      const providerOptions = optionsByProvider[selectedProvider];

                      return (
                        <Table.Row
                          key={project.id}
                          id={`project-row-${project.id}`}
                          className={
                            focusedProjectId === project.id
                              ? "bg-blue-50/80 dark:bg-blue-900/30"
                              : project.newly_synced || newlyCreatedProjectIds.includes(project.id)
                                ? "bg-green-50/50 dark:bg-green-900/20"
                                : undefined
                          }
                        >
                          <Table.Cell className="align-top">
                            <input
                              aria-label={`Select ${project.name}`}
                              type="checkbox"
                              checked={selectedProjectIds.includes(project.id)}
                              onChange={(event) =>
                                toggleProjectSelection(project.id, event.target.checked)
                              }
                            />
                          </Table.Cell>
                          <Table.Cell className="align-top">
                            <Text weight="bold">{project.name}</Text>
                            <Text as="div" size="1" color="gray">
                              Harvest ID:{" "}
                              {project.harvest_url ? (
                                <a
                                  href={project.harvest_url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="underline"
                                >
                                  {project.harvest_project_id}
                                </a>
                              ) : (
                                "Unlinked"
                              )}
                            </Text>
                          </Table.Cell>
                          <Table.Cell className="align-top">
                            <div className="space-y-1">
                              <Text as="div" size="1">
                                {project.asana_project_id
                                  ? currentAsanaOption?.name || project.asana_project_id
                                  : "Unmapped"}
                              </Text>
                              {!project.asana_project_id && recommendedAsana.id && (
                                <Text as="div" size="1" color="gray">
                                  Suggested: {recommendedAsana.name}
                                </Text>
                              )}
                              <Popover.Root>
                                <Popover.Trigger>
                                  <Button size="1" variant="soft" mt="1" className="px-3">
                                    Edit
                                  </Button>
                                </Popover.Trigger>
                                <Popover.Content size="1">
                                  <Flex direction="column" gap="2" minWidth="260px">
                                    <TextField.Root
                                      placeholder="Search Asana projects"
                                      value={asanaInput}
                                      onChange={(event) =>
                                        setFieldInput((current) => ({
                                          ...current,
                                          [asanaKey]: event.target.value,
                                        }))
                                      }
                                    />
                                    <OptionsList
                                      options={integrationOptions.asana}
                                      inputValue={asanaInput}
                                      labelFn={optionLabel}
                                      onSelect={(option) =>
                                        setFieldInput((current) => ({
                                          ...current,
                                          [asanaKey]: optionLabel(option),
                                        }))
                                      }
                                    />
                                    <Flex gap="2">
                                      <Button
                                        size="1"
                                        onClick={() => {
                                          const selected = resolveOption(
                                            integrationOptions.asana,
                                            asanaInput,
                                          );
                                          if (
                                            selected &&
                                            selected.id !== project.asana_project_id
                                          ) {
                                            updateProject(project.id, {
                                              asana_project_id: selected.id,
                                            });
                                          }
                                        }}
                                      >
                                        Save
                                      </Button>
                                      <Button
                                        size="1"
                                        variant="outline"
                                        onClick={() =>
                                          updateProject(project.id, { asana_project_id: null })
                                        }
                                      >
                                        Clear
                                      </Button>
                                    </Flex>
                                  </Flex>
                                </Popover.Content>
                              </Popover.Root>
                            </div>
                          </Table.Cell>
                          <Table.Cell className="align-top">
                            <div className="space-y-1">
                              <Text as="div" size="1">
                                {repos.length > 0 ? `${repos.length} linked` : "No repos"}
                              </Text>
                              {repos.length > 0 && (
                                <Flex gap="1" wrap="wrap">
                                  {repos.slice(0, 2).map((repo) => (
                                    <Badge
                                      key={`${repo.provider}-${repo.external_id}`}
                                      size="1"
                                      color={repo.provider === "github" ? "blue" : "indigo"}
                                    >
                                      {repo.provider}: {repo.name}
                                    </Badge>
                                  ))}
                                  {repos.length > 2 && (
                                    <Badge size="1" color="gray">
                                      +{repos.length - 2}
                                    </Badge>
                                  )}
                                </Flex>
                              )}
                              <Popover.Root>
                                <Popover.Trigger>
                                  <Button size="1" variant="soft" mt="1" className="px-3">
                                    Edit
                                  </Button>
                                </Popover.Trigger>
                                <Popover.Content size="1">
                                  <Flex direction="column" gap="2" minWidth="340px">
                                    {repos.length > 0 && (
                                      <Flex direction="column" gap="1">
                                        {repos.map((repo) => (
                                          <Flex
                                            key={`${repo.provider}-${repo.external_id}`}
                                            justify="between"
                                            align="center"
                                            gap="2"
                                          >
                                            <Text size="1">
                                              {repo.provider}: {repo.name}
                                            </Text>
                                            <Button
                                              size="1"
                                              variant="ghost"
                                              color="red"
                                              onClick={() =>
                                                updateProject(project.id, {
                                                  vcs_repositories: repos.filter(
                                                    (entry) =>
                                                      entry.external_id !== repo.external_id,
                                                  ),
                                                })
                                              }
                                            >
                                              Remove
                                            </Button>
                                          </Flex>
                                        ))}
                                      </Flex>
                                    )}
                                    {vcsLoading ? (
                                      <Flex align="center" gap="2">
                                        <Spinner size="1" />
                                        <Text size="1" color="gray">
                                          Loading repositories...
                                        </Text>
                                      </Flex>
                                    ) : (
                                      <Flex direction="column" gap="2">
                                        <Flex gap="2" align="center">
                                          <Select.Root
                                            value={selectedProvider}
                                            onValueChange={(value) =>
                                              setRepoProviderByProject((current) => ({
                                                ...current,
                                                [project.id]: value as "github" | "bitbucket",
                                              }))
                                            }
                                          >
                                            <Select.Trigger />
                                            <Select.Content>
                                              <Select.Item value="github">GitHub</Select.Item>
                                              <Select.Item value="bitbucket">Bitbucket</Select.Item>
                                            </Select.Content>
                                          </Select.Root>
                                          <TextField.Root
                                            placeholder="Select repository or paste URL"
                                            value={repoInput}
                                            onChange={(event) =>
                                              setFieldInput((current) => ({
                                                ...current,
                                                [vcsKey]: event.target.value,
                                              }))
                                            }
                                          />
                                          <Button
                                            size="1"
                                            onClick={() => {
                                              const selected =
                                                resolveOption(providerOptions, repoInput) ||
                                                resolveManualRepositoryOption(
                                                  selectedProvider,
                                                  repoInput,
                                                );
                                              if (!selected) return;

                                              const nextRepos = [
                                                ...repos,
                                                {
                                                  provider: selected.provider || selectedProvider,
                                                  external_id: selected.id,
                                                  name: selected.name,
                                                  url: selected.url || "",
                                                },
                                              ].filter(
                                                (item, index, list) =>
                                                  list.findIndex(
                                                    (entry) =>
                                                      entry.provider === item.provider &&
                                                      (entry.external_id.toLowerCase() ===
                                                        item.external_id.toLowerCase() ||
                                                        entry.name.toLowerCase() ===
                                                          item.name.toLowerCase() ||
                                                        entry.url.toLowerCase() ===
                                                          item.url.toLowerCase()),
                                                  ) === index,
                                              );
                                              updateProject(project.id, {
                                                vcs_repositories: nextRepos,
                                              });
                                              setFieldInput((current) => ({
                                                ...current,
                                                [vcsKey]: "",
                                              }));
                                            }}
                                          >
                                            Add
                                          </Button>
                                        </Flex>
                                        <Text size="1" color="gray">
                                          You can also paste a full repo URL (GitHub/Bitbucket) or
                                          `owner/repo`.
                                        </Text>
                                        <OptionsList
                                          options={providerOptions}
                                          inputValue={repoInput}
                                          labelFn={optionLabel}
                                          onSelect={(option) =>
                                            setFieldInput((current) => ({
                                              ...current,
                                              [vcsKey]: optionLabel(option),
                                            }))
                                          }
                                        />
                                      </Flex>
                                    )}
                                  </Flex>
                                </Popover.Content>
                              </Popover.Root>
                            </div>
                          </Table.Cell>
                          <Table.Cell className="align-top">
                            <div className="space-y-1">
                              <Flex gap="1" wrap="wrap">
                                <Badge size="1" color="gray">
                                  PO: {formatLeadName(leadEmail(project, "po"))}
                                </Badge>
                                <Badge size="1" color="gray">
                                  PM: {formatLeadName(leadEmail(project, "pm"))}
                                </Badge>
                                <Badge size="1" color="gray">
                                  FE: {formatLeadName(leadEmail(project, "fe"))}
                                </Badge>
                                <Badge size="1" color="gray">
                                  BE: {formatLeadName(leadEmail(project, "be"))}
                                </Badge>
                              </Flex>
                              <Popover.Root>
                                <Popover.Trigger>
                                  <Button size="1" variant="soft" mt="1" className="px-3">
                                    Edit
                                  </Button>
                                </Popover.Trigger>
                                <Popover.Content size="1">
                                  <Box minWidth="320px">
                                    <Flex direction="column" gap="2">
                                      {[
                                        {
                                          role: "po",
                                          label: "Project Owner (PO)",
                                          key: "project_owner_id",
                                        },
                                        {
                                          role: "pm",
                                          label: "Project Manager (PM)",
                                          key: "project_manager_id",
                                        },
                                        {
                                          role: "fe",
                                          label: "Frontend Lead (FE)",
                                          key: "frontend_lead_id",
                                        },
                                        {
                                          role: "be",
                                          label: "Backend Lead (BE)",
                                          key: "backend_lead_id",
                                        },
                                      ].map((item) => (
                                        <div key={`${project.id}-${item.key}`}>
                                          <Text as="label" size="1" weight="medium">
                                            {item.label}
                                          </Text>
                                          <Select.Root
                                            value={
                                              leadValue(
                                                project,
                                                item.role as "po" | "pm" | "fe" | "be",
                                              ) || undefined
                                            }
                                            onValueChange={(value) =>
                                              updateProject(project.id, {
                                                [item.key]: value ? parseInt(value, 10) : null,
                                              })
                                            }
                                          >
                                            <Select.Trigger mt="1" placeholder="Select user" />
                                            <Select.Content>
                                              <Select.Item value="">-- Unassigned --</Select.Item>
                                              {users.map((user) => (
                                                <Select.Item
                                                  key={user.id}
                                                  value={user.id.toString()}
                                                >
                                                  {user.email}
                                                </Select.Item>
                                              ))}
                                            </Select.Content>
                                          </Select.Root>
                                        </div>
                                      ))}
                                    </Flex>
                                  </Box>
                                </Popover.Content>
                              </Popover.Root>
                            </div>
                          </Table.Cell>
                          <Table.Cell className="align-top">
                            <Select.Root
                              value={project.project_group_id?.toString() || ""}
                              onValueChange={(value) =>
                                updateProject(project.id, {
                                  project_group_id: value ? parseInt(value, 10) : null,
                                })
                              }
                            >
                              <Select.Trigger placeholder="Select group" />
                              <Select.Content>
                                {projectGroups.map((group) => (
                                  <Select.Item key={group.id} value={group.id.toString()}>
                                    {group.name}
                                  </Select.Item>
                                ))}
                              </Select.Content>
                            </Select.Root>
                          </Table.Cell>
                          <Table.Cell className="align-top">
                            <div className="space-y-1">
                              <Text as="div" size="1">
                                {project.quickbooks_customer_id
                                  ? currentQbOption?.name || project.quickbooks_customer_id
                                  : "Unmapped"}
                              </Text>
                              {!project.quickbooks_customer_id && recommendedQb.id && (
                                <Text as="div" size="1" color="gray">
                                  Suggested: {recommendedQb.name}
                                </Text>
                              )}
                              <Popover.Root>
                                <Popover.Trigger>
                                  <Button size="1" variant="soft" mt="1" className="px-3">
                                    Edit
                                  </Button>
                                </Popover.Trigger>
                                <Popover.Content size="1">
                                  <Flex direction="column" gap="2" minWidth="260px">
                                    <TextField.Root
                                      placeholder="Search QB clients"
                                      value={qbInput}
                                      onChange={(event) =>
                                        setFieldInput((current) => ({
                                          ...current,
                                          [qbKey]: event.target.value,
                                        }))
                                      }
                                    />
                                    <OptionsList
                                      options={integrationOptions.quickbooks}
                                      inputValue={qbInput}
                                      labelFn={optionLabel}
                                      onSelect={(option) =>
                                        setFieldInput((current) => ({
                                          ...current,
                                          [qbKey]: optionLabel(option),
                                        }))
                                      }
                                    />
                                    <Flex gap="2">
                                      <Button
                                        size="1"
                                        onClick={() => {
                                          const selected = resolveOption(
                                            integrationOptions.quickbooks,
                                            qbInput,
                                          );
                                          if (
                                            selected &&
                                            selected.id !== project.quickbooks_customer_id
                                          ) {
                                            updateProject(project.id, {
                                              quickbooks_customer_id: selected.id,
                                            });
                                          }
                                        }}
                                      >
                                        Save
                                      </Button>
                                      <Button
                                        size="1"
                                        variant="outline"
                                        onClick={() =>
                                          updateProject(project.id, {
                                            quickbooks_customer_id: null,
                                          })
                                        }
                                      >
                                        Clear
                                      </Button>
                                    </Flex>
                                  </Flex>
                                </Popover.Content>
                              </Popover.Root>
                            </div>
                          </Table.Cell>
                        </Table.Row>
                      );
                    })}
                </Fragment>
              ))}
            </Table.Body>
          </Table.Root>
          {activeProjectAction && (
            <Text size="1" color="gray">
              Saving changes...
            </Text>
          )}
          {vcsLoading && (
            <Text size="1" color="gray">
              Loading VCS options...
            </Text>
          )}
        </div>
      </AppLayout>
    </>
  );
}
